import logger from '../lib/logger';
import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Facebook, Instagram, MessageCircle, Loader2, AlertCircle } from 'lucide-react';
import { contactInfo, socialLinks } from '../data/mock';
import { submitContactForm } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await submitContactForm(formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      logger.error('Failed to submit contact form:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '50vh' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2f23] to-[#2a5a3d]" />
        <div className="absolute inset-0 z-2 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="hero-content container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-6">Contact Us</h1>
            <p className="text-white/70 text-lg">
              We'd love to hear from you. Reach out with any questions, suggestions, or to learn more about our community.
            </p>
            <div className="islamic-divider mt-8">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid-2">
            {/* Contact Information */}
            <div>
              <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Reach Out</p>
              <h2 className="heading-2 mb-8">Get in Touch</h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 border border-[#c5a572] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#c5a572]" />
                  </div>
                  <div>
                    <h3 className="text-lg text-[#1a2f23] mb-1 tracking-wide">Our Location</h3>
                    <p className="text-[#5a6d62] tracking-wide">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 border border-[#c5a572] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#c5a572]" />
                  </div>
                  <div>
                    <h3 className="text-lg text-[#1a2f23] mb-1 tracking-wide">Phone</h3>
                    <p className="text-[#5a6d62] tracking-wide">{contactInfo.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 border border-[#c5a572] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#c5a572]" />
                  </div>
                  <div>
                    <h3 className="text-lg text-[#1a2f23] mb-1 tracking-wide">Email</h3>
                    <p className="text-[#5a6d62] tracking-wide">{contactInfo.email}</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-lg text-[#1a2f23] mb-4">Connect With Us</h3>
                <div className="flex items-center gap-3">
                  <a 
                    href={socialLinks.facebook}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-14 h-14 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6 text-[#c5a572]" />
                  </a>
                  <a 
                    href={socialLinks.instagram}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-14 h-14 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6 text-[#c5a572]" />
                  </a>
                  <a 
                    href={socialLinks.whatsapp}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-14 h-14 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                    aria-label="WhatsApp"
                  >
                    <MessageCircle className="w-6 h-6 text-[#c5a572]" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="card card-gold">
              <h3 className="text-2xl text-[#1a2f23] mb-2">Send us a Message</h3>
              <p className="text-sm text-[#5a6d62] mb-8">Fill out the form below and we'll get back to you shortly.</p>
              
              {submitted ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#c5a572] flex items-center justify-center">
                    <Send className="w-10 h-10 text-[#c5a572]" />
                  </div>
                  <h3 className="text-2xl text-[#1a2f23] mb-2">Message Sent!</h3>
                  <p className="text-[#5a6d62]">Thank you for reaching out. We'll respond as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-sm uppercase tracking-wider text-[#5a6d62]">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        className="mt-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm uppercase tracking-wider text-[#5a6d62]">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        className="mt-2"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="phone" className="text-sm uppercase tracking-wider text-[#5a6d62]">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(604) 555-0123"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-sm uppercase tracking-wider text-[#5a6d62]">Subject *</Label>
                      <Select value={formData.subject} onValueChange={(value) => setFormData({ ...formData, subject: value })}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="programs">Programs & Events</SelectItem>
                          <SelectItem value="membership">Membership</SelectItem>
                          <SelectItem value="donation">Donations</SelectItem>
                          <SelectItem value="madrassah">Madrassah</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm uppercase tracking-wider text-[#5a6d62]">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="How can we help you?"
                      rows={5}
                      className="mt-2"
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full btn-primary mt-6" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
